import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoiceDocument } from '../../api/invoice/invoiceApiSlice';
import { STATION_TIME_ZONE } from '../../utils/stationTime';

interface Props {
    documents: InvoiceDocument[];
    /** Σ of every document's gross, from the server (computed in cents). */
    netGross: string;
    lang: 'de' | 'en';
    onDownload: (doc: InvoiceDocument) => void;
}

/**
 * Every invoice document of one booking, oldest first (#1643): the original,
 * its cancellation, a replacement. An issued invoice is never edited, so this
 * history IS the record — and the net row shows what the chain amounts to.
 */
const InvoiceDocumentTable = ({ documents, netGross, lang, onDownload }: Readonly<Props>) => {
    const { t } = useTranslation();
    const locale = lang === 'en' ? 'en-GB' : 'de-DE';
    const money = (value: string, currency: string) =>
        new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(value));
    const day = (iso: string) =>
        new Intl.DateTimeFormat(locale, { timeZone: STATION_TIME_ZONE }).format(new Date(iso));

    const typeLabel = (doc: InvoiceDocument) =>
        t(doc.documentType === 'cancellation' ? 'account.invoice_type_cancellation' : 'account.invoice_type_invoice');

    const statusLabel = (doc: InvoiceDocument) => {
        if (doc.status === 'canceled') return t('account.invoice_status_canceled');
        if (doc.status === 'corrected') return t('account.invoice_status_corrected');
        return t('account.invoice_status_issued');
    };

    return (
        <>
            <table className={'w-full text-sm mt-2'}>
                <thead>
                    <tr className={'text-left text-foreground/60 font-medium border-b border-foreground/5'}>
                        <th className={'py-2 pr-3 font-medium'}>{t('account.invoice_issued_number')}</th>
                        <th className={'py-2 pr-3 font-medium'}>{t('account.invoice_document_type')}</th>
                        <th className={'py-2 pr-3 font-medium'}>{t('account.invoice_document_status')}</th>
                        <th className={'py-2 pr-3 font-medium'}>{t('account.invoice_issued_at')}</th>
                        <th className={'py-2 pr-3 font-medium text-right'}>{t('account.invoice_document_amount')}</th>
                        <th className={'py-2'} />
                    </tr>
                </thead>
                <tbody>
                    {documents.map((doc) => (
                        <tr key={doc.id} className={'border-b border-foreground/5 text-foreground'}>
                            <td className={'py-2 pr-3 font-medium'}>{doc.number}</td>
                            <td className={'py-2 pr-3'}>{typeLabel(doc)}</td>
                            <td className={'py-2 pr-3'}>{statusLabel(doc)}</td>
                            <td className={'py-2 pr-3'}>{day(doc.issuedAt)}</td>
                            <td className={'py-2 pr-3 text-right'}>{money(doc.totalGross, doc.currency)}</td>
                            <td className={'py-2 text-right'}>
                                <button
                                    type={'button'}
                                    className={'text-primary hover:underline'}
                                    onClick={() => onDownload(doc)}
                                >
                                    {t('account.invoice_download_short')}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
                {documents.length > 1 ? (
                    <tfoot>
                        <tr className={'text-foreground'}>
                            <td className={'py-2 pr-3 font-medium'} colSpan={4}>{t('account.invoice_chain_net')}</td>
                            <td className={'py-2 pr-3 text-right font-medium'}>
                                {money(netGross, documents[0]?.currency ?? 'EUR')}
                            </td>
                            <td />
                        </tr>
                    </tfoot>
                ) : null}
            </table>
            <p className={'text-foreground/60 text-xs mt-2 mb-3'}>{t('account.invoice_archived_note')}</p>
        </>
    );
};

export default InvoiceDocumentTable;
